import React, {useCallback, useEffect, useState} from 'react';
import {Button, ToggleButton, ToggleButtonGroup, Box} from '@mui/material';
import {
    useAssignRolePermissionsMutation,
    useGetPermissionsQuery,
    useGetRolesQuery
} from '../../../../api/services/accountService';
import {ROLES, ROLES_LABELS} from '../../../../constants/roles';
import PropTypes from 'prop-types';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';

/* eslint-disable react/prop-types */

const COLUMNS_ORDER = {
    [ROLES.SCHOOL_ADMIN]: 1,
    [ROLES.MEMBER_OF_STAFF]: 2,
    [ROLES.SENIOR_MEMBER_OF_STAFF]: 3,
    [ROLES.PARENT]: 4
}

const PermissionSelect = ({permissions, value, onChange}) =>
    (
        <ToggleButtonGroup
            color="primary"
            value={value}
            exclusive
            onChange={(e, permission) => onChange(permission)}
        >
            {
                permissions.map(permission => (
                    <ToggleButton
                        sx={{
                            textTransform: 'none',
                            '&.Mui-selected': {
                                backgroundColor: '#186BBE',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: '#155FA7'
                                }
                            }
                        }}
                        key={permission.value}
                        value={permission.value}
                        disabled={permission.disabled}
                        size="small"
                    >
                        {permission.name}
                    </ToggleButton>
                ))
            }
        </ToggleButtonGroup>
    );

PermissionSelect.propTypes = {
    permissions: PropTypes.array.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
};

const AccessSettings = () => {
    const {data: permissionModules = [], isLoading: isPermissionsLoading} = useGetPermissionsQuery();
    const {data: roles = [], isLoading: isRolesLoading} = useGetRolesQuery();
    const [assignPermissions, {isLoading: isAssignPermissionsInProgress}] = useAssignRolePermissionsMutation();
    const [permissions, setPermissions] = useState({});
    const [originalPermissionsSnapshot, setOriginalPermissionsSnapshot] = useState({});
    const isLoading = isPermissionsLoading ||
        isRolesLoading ||
        isAssignPermissionsInProgress;

    const handlePermissionChange = useCallback((roleId, module, value) => {
        if (value) {
            setPermissions(prevPermissions => ({
                ...prevPermissions,
                [roleId]: {
                    ...prevPermissions[roleId],
                    [module]: value
                }
            }));
        }
    }, []);

    useEffect(() => {
        if (permissionModules.length > 0) {
            const permissions = permissionModules.reduce((acc, data) => {
                data.permissions.forEach(permission => {
                    permission.roleIds.forEach(roleId => {
                        if (!acc[roleId]) {
                            acc[roleId] = {};
                        }
                        acc[roleId][data.key] = permission.value;
                    });
                });
                return acc;
            }, {});

            setPermissions(permissions);
            setOriginalPermissionsSnapshot(permissions);
        }
    }, [permissionModules]);

    const columns = [
        {
            accessorKey: 'description',
            header: 'Actions',
            flex: 1,
            editable: false,
            sortable: false,
        },
        ...roles
            .filter(x => x.name === ROLES.SCHOOL_ADMIN ||
                x.name === ROLES.MEMBER_OF_STAFF ||
                x.name === ROLES.PARENT ||
                x.name === ROLES.SENIOR_MEMBER_OF_STAFF
            )
            .map(x => ({...x, order: COLUMNS_ORDER[x.name] ?? 0}))
            .sort((a, b) => a.order - b.order)
            .map(({name, id: roleId}) => ({
                accessorKey: name,
                header: ROLES_LABELS[name],
                muiTableHeadCellProps: {
                    align: 'center',
                },
                muiTableBodyCellProps: {
                    align: 'center',
                },
                editable: false,
                sortable: false,
                Header: () => <strong>{ROLES_LABELS[name]}</strong>,
                Cell: ({row}) =>
                    row.original[name] && (
                        <PermissionSelect
                            permissions={row.original[name]}
                            value={permissions[roleId]?.[row.original.module]}
                            onChange={(value) => handlePermissionChange(roleId, row.original.module, value)}
                        />
                    )
            }))
    ];

    const handleAssignPermissions = async () => {
        const changedPermissions = Object
            .entries(permissions)
            .reduce((changedPermissions, [roleId, modules]) => {
                Object
                    .values(modules)
                    .forEach((value) => {
                        if (!changedPermissions[roleId]) {
                            changedPermissions[roleId] = [];
                        }

                        changedPermissions[roleId].push(value);
                    });
                return changedPermissions;
            }, {});

        await assignPermissions({
            roleClaims: Object.entries(changedPermissions)
                .map(([roleId, claims]) => ({
                    roleId,
                    claims: claims.filter(x => x)
                }))
        });
        setOriginalPermissionsSnapshot({});
    };

    const checkIfPermissionsChanged = () => {
        for (const [roleId, modules] of Object.entries(permissions)) {
            for (const [module, value] of Object.entries(modules)) {
                const original = originalPermissionsSnapshot[roleId]?.[module];

                if (original !== undefined && value !== original) {
                    return true;
                }
            }
        }

        return false;
    };

    return (
        <>
            <Box
                sx={{
                    mt: 2,
                    mb: 2,
                }}
            >
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    startIcon={<AddTaskIcon />}
                    onClick={handleAssignPermissions}
                    disabled={isLoading || !checkIfPermissionsChanged()}
                >
                    Assign Permissions
                </Button>
            </Box>
            <AdmicityTableV2
                tableProps={{
                    enableSorting: false,
                    enableColumnResizing: false,
                    enableBottomToolbar: false,
                    enableFilters: false,
                    enableHiding: false,
                    enableFullScreenToggle: false,
                    muiTableBodyCellProps: {
                        sx: {
                            borderBottom: 'none',
                            paddingTop: 1,
                            paddingBottom: 1
                        },
                    },
                }}
                data={permissionModules && roles.length > 0
                    ? permissionModules.map((module, index) => ({
                        id: index,
                        description: module.description,
                        module: module.key,
                        ...roles
                            .reduce((roleItems, role) => ({
                                ...roleItems,
                                [role.name]: module.permissions.map(permission => ({
                                    name: permission.name,
                                    value: permission.value,
                                    disabled: permission.disabledForRoleIds.includes(role.id)
                                }))
                            }), {})
                    }))
                    : []}
                columns={columns}
                isLoading={isLoading}
                hideFooterPagination
            />
        </>
    );
};

export default AccessSettings;